import { postProviderJson, providerError } from './providerHttp';
import { toOpenAiMessages, toOpenAiTools, emitOpenAiChoice } from './openai';
import { ensureFreshToken, getAccessToken } from '../../tokenRefresher';
import { getApibooStatus, invalidateApibooStatus, noteApibooQuota } from '../apibooStatus';
import { setUpgradeModalOpen, setUpgradeReason } from '../../../store/uiModalStore';

/**
 * The Apiboo assistant adapter (22.08.2026) — the keyless provider.
 *
 * Same adapter surface as providers/openai.js minus the key and the model:
 * the server (backend_phoenix `POST /api/v1/ai/complete`) holds an OpenRouter
 * key, forces the model, and relays OpenRouter's `choices[0]` verbatim. The
 * wire format is OpenAI's, so the translation is the OpenAI adapter's — the
 * message/tool mapping and the 1024-char description clamp are reused, not
 * copied. The request body is EXACTLY `{ messages, tools }`: the server
 * answers 422 to anything else (model, stream, max_tokens, provider,
 * tool_choice), so nothing may be added here.
 *
 * Transport: providerHttp, never apiService. apiService's axios adapter drops
 * timeouts under Tauri, so a model turn there dies on the Rust client's 30 s
 * default; providerHttp carries the 300 s `timeout_secs` the other adapters
 * already rely on. `browserFallback` lets the browser dev build use fetch
 * (the API allows that origin; the third-party providers do not).
 */

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.apiboo.com/api/v1';
export const COMPLETE_URL = `${API_BASE_URL}/ai/complete`;

export const SIGN_IN_MESSAGE =
  'Sign in to use the Apiboo assistant, or add your own key in Settings → AI Chat.';
export const UNAVAILABLE_MESSAGE =
  'The Apiboo assistant is not available right now — add your own key in Settings → AI Chat.';
const UPSTREAM_MESSAGE = 'The Apiboo assistant could not reach the model provider.';

function retryAfterSeconds(headers) {
  const raw = headers?.['retry-after'] ?? headers?.['Retry-After'];
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Map the contract's error statuses onto messages the panel can show as-is. */
export function apibooError(status, json, headers) {
  const serverMessage = typeof json?.error === 'string' ? json.error : '';
  switch (status) {
    case 401:
      return new Error(SIGN_IN_MESSAGE);
    case 402: {
      // Same trigger the 402 `{error, upgrade_required}` shape drives
      // everywhere else in the app (useWorkspaces, useCollections …).
      setUpgradeReason(serverMessage);
      setUpgradeModalOpen(true);
      return new Error(serverMessage || 'Your Apiboo assistant quota for this month is used up.');
    }
    case 429: {
      const secs = retryAfterSeconds(headers);
      return new Error(
        secs
          ? `The Apiboo assistant is busy — try again in ${secs}s.`
          : 'The Apiboo assistant is busy — try again in a moment.'
      );
    }
    case 503:
      // Not configured (or switched off) on the server: the status cache is
      // stale the moment this arrives, so the next resolution must re-ask.
      invalidateApibooStatus();
      return new Error(UNAVAILABLE_MESSAGE);
    case 502:
      return new Error(serverMessage || UPSTREAM_MESSAGE);
    default:
      return providerError('Apiboo', status, serverMessage);
  }
}

/** Buffered only for now — the contract reserves `stream` (422 if sent). */
export function supportsStreaming() {
  return false;
}

export async function send({ system, reference, tools, messages, onTextDelta, onToolUse, onUsage }) {
  const fullSystem = reference ? `${system}\n\n${reference}` : system;
  const body = {
    messages: toOpenAiMessages(fullSystem, messages),
    tools: toOpenAiTools(tools),
  };

  await ensureFreshToken().catch(() => {});
  const token = getAccessToken();
  if (!token) throw new Error(SIGN_IN_MESSAGE);

  const { status, json, headers } = await postProviderJson(
    COMPLETE_URL,
    { authorization: `Bearer ${token}` },
    body,
    { browserFallback: true }
  );
  if (status >= 400 || !json) throw apibooError(status, json, headers);

  noteApibooQuota(json.quota);
  // The server relays OpenRouter's `choices[0]` as `choice`; re-wrap it so
  // the OpenAI adapter's response mapping applies unchanged.
  return emitOpenAiChoice({ choices: [json.choice], usage: json.usage }, { onTextDelta, onToolUse, onUsage });
}

/**
 * "Test key" for a keyless provider: is the assistant configured and reachable
 * for this account? Throws with the reason when it is not.
 */
export async function testKey() {
  const status = await getApibooStatus({ force: true });
  if (status.available) return true;
  if (status.reason === 'unauthenticated') throw new Error(SIGN_IN_MESSAGE);
  if (status.reason === 'unreachable') throw new Error('Could not reach the Apiboo server.');
  throw new Error(UNAVAILABLE_MESSAGE);
}
